import { type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { Badge, Text, Tooltip } from '@cowbell/ui';
import StatusBadge from './StatusBadge';
import type { StudioEntityMeta, StudioTag } from './entity-meta';
import styles from './StudioEntityCard.module.css';

export type StudioEntityVariant = 'card' | 'row' | 'compact';

interface StudioEntityCardProps {
  readonly entity: StudioEntityMeta;
  readonly variant: StudioEntityVariant;
  readonly onSelect?: (id: string) => void;
  /** Menu / buttons slot, rendered top-right alongside status. */
  readonly actions?: ReactNode;
  /**
   * OMNI-828 fix round 1: optional `aria-expanded` value for the
   * interactive root. Only ever applied alongside the other interactive-only
   * attributes below (role/tabIndex/onClick/onKeyDown) — i.e. only when
   * `onSelect` is present — so a card rendered without `onSelect` still
   * isn't announced as an interactive control. Omit entirely to keep
   * today's behavior: no consumer other than DocCard passes this, and none
   * of them should render an `aria-expanded` attribute at all.
   */
  readonly ariaExpanded?: boolean;
}

/**
 * What each variant shows, keyed by variant so picking the shape is a lookup,
 * never a ternary chain. The real distinguishing axis is padding + description
 * line-clamp, not a grid-vs-list layout — `card` and `row` render identical
 * fields (icon, title+subtitle, description, tag row, status top-right) and
 * each shows up in both grid and single-column list consumers (e.g. `card`
 * backs both the grid-laid-out Integration Catalog and the single-column
 * Knowledge Base `DocCard`):
 * - `card` — medium padding (`--mantine-spacing-md`), 2-line clamped
 *   description.
 * - `row` — larger padding (`--mantine-spacing-lg`), single-line clamped
 *   description.
 * - `compact` — the only variant that omits content: icon+title+status only
 *   (no subtitle, description, or tags), tight padding.
 */
interface VariantLayout {
  readonly rootClassName: string;
  readonly showSubtitle: boolean;
  readonly showDescription: boolean;
  readonly descriptionClamp: number;
  readonly showTags: boolean;
}

const VARIANT_LAYOUT: Record<StudioEntityVariant, VariantLayout> = {
  card: {
    rootClassName: styles.card,
    showSubtitle: true,
    showDescription: true,
    descriptionClamp: 2,
    showTags: true,
  },
  row: {
    rootClassName: styles.row,
    showSubtitle: true,
    showDescription: true,
    descriptionClamp: 1,
    showTags: true,
  },
  compact: {
    rootClassName: styles.compact,
    showSubtitle: false,
    showDescription: false,
    descriptionClamp: 0,
    showTags: false,
  },
};

/**
 * Tag tone -> Badge rendering, keyed the same lookup way as VARIANT_LAYOUT.
 * `category` reuses the app's established "filled-light gray pill" (the same
 * treatment as the Skills list's category badge / AgentCard's "Custom"
 * badge). `scope` is an outline pill with a monospace label — appropriate
 * for a short raw token like `claims:view`. NOTE it has NO production producer
 * left — all three adapters emit `capability`, and only a test fixture still
 * exercises `scope`. It is kept deliberately, as the rendering a caller would
 * need to show a raw scope verbatim, not because something still does; delete
 * it and the fixture together if that stops being worth carrying.
 * `capability` (OMNI-831 Task 2) is the plain-English
 * replacement — same light pill as `category`, deliberately NOT monospace: a
 * monospace "Manage claims" sentence reads as broken and wraps the tag row
 * across multiple lines on a dense card grid, the opposite of what
 * `describeScope` is for. `info` is a transparent, dimmed-text pill for
 * lower-emphasis tags. Routing all four through @cowbell/ui's Badge (instead
 * of hand-rolled colors) keeps them scheme-aware for free rather than
 * risking a light-on-dark hardcode (see the Mantine color-token gotcha in
 * AGENTS.md).
 */
const TAG_TONE_STYLE: Record<
  StudioTag['tone'],
  {
    readonly variant: 'light' | 'outline' | 'transparent';
    readonly mono: boolean;
  }
> = {
  category: { variant: 'light', mono: false },
  scope: { variant: 'outline', mono: true },
  capability: { variant: 'light', mono: false },
  info: { variant: 'transparent', mono: false },
};

function TagPill({ tag }: Readonly<{ tag: StudioTag }>) {
  const { variant, mono } = TAG_TONE_STYLE[tag.tone];
  const badge = (
    <Badge
      variant={variant}
      color="gray"
      size="xs"
      tt="none"
      className={mono ? styles.tagMono : undefined}
      data-tag-tone={tag.tone}
    >
      {tag.label}
    </Badge>
  );

  // `tooltip` is additive (entity-meta.ts) and absent for every pre-OMNI-831
  // tag producer, so this branch — and thus any Tooltip usage at all — never
  // fires for existing callers, preserving today's render output exactly.
  if (!tag.tooltip) return badge;

  return (
    <Tooltip label={tag.tooltip} withArrow>
      {badge}
    </Tooltip>
  );
}

function joinClassNames(
  ...names: ReadonlyArray<string | false | undefined>
): string {
  return names.filter(Boolean).join(' ');
}

/**
 * Two different raw scopes can humanize to the same visible label — an agent
 * holding both `claims:read` and `claims:view` gets "View claims" twice from
 * `describeScope`. That rendered as two identical-looking pills (a visible
 * defect, not just a duplicate-React-key warning, since the tag row is keyed
 * by label). Collapse same-tone/same-label tags into one pill and merge their
 * raw scopes into the surviving tooltip, so the duplicate disappears without
 * making either underlying scope undiscoverable.
 *
 * Keyed on tone AND label so a `category` tag and a `capability` tag that
 * happen to share wording stay separate — they are different statements.
 *
 * IF YOU ADD A TAG-COUNT CAP (a `maxTags`, a `.slice()`), IT MUST RUN AFTER
 * THIS. There is no cap today — `visibleTags` is a boolean gate, not a slice —
 * so the ordering hazard is latent rather than live. Capping first would let a
 * duplicate consume a visible slot and leave any "+N more" counting raw tags
 * instead of the pills a reader can see. `CapabilityChips`, the other renderer
 * of this data, already has a cap and documents the same constraint; this is
 * the one place the two could realistically drift apart.
 */
// Exported for its own unit test only — deliberately NOT re-exported from
// studio-components/index.ts, since it is an internal rendering detail rather
// than part of this directory's public surface. Asserting the merged tooltip
// through the rendered Tooltip is not viable: Mantine only mounts a tooltip's
// label into the DOM via floating-ui on real hover, which jsdom does not drive.
export function dedupeTags(tags: readonly StudioTag[]): StudioTag[] {
  const merged = new Map<string, StudioTag>();

  tags.forEach((tag) => {
    const key = `${tag.tone}::${tag.label}`;
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, tag);
      return;
    }
    const tooltips = [existing.tooltip, tag.tooltip].filter(
      (value): value is string => !!value
    );
    merged.set(key, {
      ...existing,
      tooltip: tooltips.length
        ? Array.from(new Set(tooltips)).join(', ')
        : existing.tooltip,
    });
  });

  // Array.from, not spread: this repo's transform target does not downlevel
  // iterator spread, so `[...map.values()]` silently yields an empty array.
  return Array.from(merged.values());
}

/**
 * The canonical Moo Studio entity renderer. Every list/grid item across
 * Agents, Skills, Tools, Integrations, and Tasks eventually renders through
 * this component with a `StudioEntityMeta` produced by a per-surface adapter,
 * replacing each view's own bespoke card/row markup with one shared
 * component, tag vocabulary, and accessibility pattern.
 */
export default function StudioEntityCard({
  entity,
  variant,
  onSelect,
  actions,
  ariaExpanded,
}: StudioEntityCardProps) {
  const layout = VARIANT_LAYOUT[variant];

  function stopRowClick(event: MouseEvent<HTMLDivElement>) {
    event.stopPropagation();
  }

  // Mirrors stopRowClick for the keyboard path: without this, an Enter
  // keydown on a nested focusable control inside `actions` (e.g. a future
  // kebab-menu button) bubbles past `.headerActions` — which only stopped
  // `onClick` — straight up to the root's own onKeyDown below, incorrectly
  // firing onSelect for a keypress that never targeted the card itself.
  function stopRowKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    event.stopPropagation();
  }

  // Accessibility pattern copied from
  // components/ask-moo/components/AgentCard.tsx (role="button" + tabIndex
  // + Enter-key handling) — only applied when the card is actually
  // clickable, so a card rendered without `onSelect` isn't announced as an
  // interactive control it can't act on. `onSelect` is narrowed to defined
  // inside this branch, so both handlers call it directly (no `?.`).
  const interactiveProps = onSelect
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: () => onSelect(entity.id),
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === 'Enter') onSelect(entity.id);
        },
        // React omits an attribute entirely when its value is `undefined`
        // (same convention as ThinkingBlock.tsx's own
        // `aria-expanded={hasReasoning ? open : undefined}`), so leaving
        // `ariaExpanded` unset here renders no attribute at all — today's
        // behavior for every consumer that doesn't pass it.
        'aria-expanded': ariaExpanded,
      }
    : undefined;

  const visibleTags = layout.showTags ? entity.tags : undefined;
  const hasHeaderExtras = !!entity.status || !!actions;

  return (
    <div
      className={joinClassNames(
        styles.root,
        layout.rootClassName,
        !!onSelect && styles.interactive
      )}
      data-entity-variant={variant}
      {...interactiveProps}
    >
      {entity.icon && <div className={styles.icon}>{entity.icon}</div>}
      <div className={styles.content}>
        <div className={styles.titleRow}>
          <div className={styles.titleGroup}>
            <Text fw={600} size="sm" lineClamp={1}>
              {entity.title}
            </Text>
            {layout.showSubtitle && entity.subtitle && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                {entity.subtitle}
              </Text>
            )}
          </div>
          {hasHeaderExtras && (
            // Swallow clicks and keydowns on status/actions so they don't
            // also trigger the row's own onSelect (same guard as AgentCard's
            // actions rail, extended to the keyboard path).
            <div
              className={styles.headerActions}
              onClick={stopRowClick}
              onKeyDown={stopRowKeyDown}
            >
              {entity.status && (
                <StatusBadge
                  kind={entity.status.kind}
                  label={entity.status.label}
                />
              )}
              {actions}
            </div>
          )}
        </div>
        {layout.showDescription && entity.description && (
          <Text size="sm" c="dimmed" lineClamp={layout.descriptionClamp}>
            {entity.description}
          </Text>
        )}
        {visibleTags && visibleTags.length > 0 && (
          <div className={styles.tagRow}>
            {dedupeTags(visibleTags).map((tag) => (
              <TagPill key={`${tag.tone}::${tag.label}`} tag={tag} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
